import { Domain } from "constants/filters";
import { StudyType } from "helpers/api";
import { CountryCode } from "helpers/sharedDataConstants";
import { Filters } from "store/slices/search";

// Handles removing an individual filter or an item from a list filter
export function handleRemoveFilter(
  key: keyof Filters,
  value: string | number | undefined,
  appliedFilters: Filters,
  changeFilter: (key: keyof Filters, value: Filters[keyof Filters]) => void,
  removeFilter: (key: keyof Filters) => void
) {
  const currentValue = appliedFilters[key];

  if (Array.isArray(currentValue)) {
    // **Remove only the selected value from the array**
    if (key === "studyTypes") {
      const updatedValue = (currentValue as StudyType[]).filter(
        (item) => item !== value
      );
      changeFilter(key, updatedValue.length ? updatedValue : undefined);
    } else if (key === "domains") {
      const updatedValue = (currentValue as Domain[]).filter(
        (item) => item !== value
      );
      changeFilter(key, updatedValue.length ? updatedValue : undefined);
    } else if (key === "countries") {
      const updatedValue = (currentValue as CountryCode[]).filter(
        (item) => item.code !== value
      );
      changeFilter(key, updatedValue.length ? updatedValue : undefined);
    }
  } else {
    // **Remove entire filter for non-list types**
    removeFilter(key);
  }
}

// Helper function to format filter labels
export function getFilterLabels(
  filters: Filters
): { label: string; key: keyof Filters; value?: string | number }[] {
  const labels: {
    label: string;
    key: keyof Filters;
    value?: string | number;
  }[] = [];

  if (filters.citationsMin)
    labels.push({
      label: `Citations ≥ ${filters.citationsMin}`,
      key: "citationsMin",
    });
  if (filters.yearMin)
    labels.push({ label: `From ${filters.yearMin}`, key: "yearMin" });
  if (filters.yearMax)
    labels.push({ label: `Until ${filters.yearMax}`, key: "yearMax" });

  if (filters.studyTypes?.length) {
    filters.studyTypes.forEach((type) =>
      labels.push({
        label: `${formatStudyType(type)}`,
        key: "studyTypes",
        value: type,
      })
    );
  }

  if (filters.domains?.length) {
    filters.domains.forEach((domain) =>
      labels.push({
        label: `${formatDomain(domain)}`,
        key: "domains",
        value: domain,
      })
    );
  }

  if (filters.countries?.length) {
    filters.countries.forEach((country) =>
      labels.push({
        label: `${country.displayName}`,
        key: "countries",
        value: country.code,
      })
    );
  }

  if (filters.openAccess)
    labels.push({ label: "Open Access", key: "openAccess" });
  if (filters.excludePreprints)
    labels.push({ label: "Exclude Preprints", key: "excludePreprints" });
  if (filters.hasValidChatPdf)
    labels.push({ label: "Ask Paper Available", key: "hasValidChatPdf" });
  if (filters.filterControlledStudies)
    labels.push({ label: "Controlled", key: "filterControlledStudies" });
  if (filters.filterHumanStudies)
    labels.push({ label: "Humans", key: "filterHumanStudies" });

  if (filters.sampleSizeMin)
    labels.push({
      label: `Sample size ≥ ${filters.sampleSizeMin}`,
      key: "sampleSizeMin",
    });
  if (filters.sampleSizeMax)
    labels.push({
      label: `Sample size ≤ ${filters.sampleSizeMax}`,
      key: "sampleSizeMax",
    });

  if (filters.sjrBestQuartileMin || filters.sjrBestQuartileMax) {
    labels.push({
      label: `Q${filters.sjrBestQuartileMin ?? 1}-Q${
        filters.sjrBestQuartileMax ?? 4
      } Journals`,
      key: "sjrBestQuartileMax",
    });
  }

  if (filters.durationMin)
    labels.push({
      label: `Duration ≥ ${formatTimeWithUnitLabel(
        filters.durationMin,
        filters.durationUnit
      )}`,
      key: "durationMin",
    });

  if (filters.durationMax)
    labels.push({
      label: `Duration < ${formatTimeWithUnitLabel(
        filters.durationMax,
        filters.durationUnit
      )}`,
      key: "durationMax",
    });

  return labels;
}

// **Helper Functions**
function formatTimeWithUnitLabel(value: number, unit: string = "day") {
  return `${value} ${unit}${value > 1 ? "s" : ""}`;
}

function formatStudyType(type: StudyType) {
  const studyTypeLabels: Record<StudyType, string> = {
    [StudyType.RCT]: "Randomized Controlled Trial",
    [StudyType.META_ANALYSIS]: "Meta-Analysis",
    [StudyType.SYSTEMATIC_REVIEW]: "Systematic Review",
    [StudyType.CASE_STUDY]: "Case Study",
    [StudyType.OBSERVATIONAL_STUDY]: "Observational Study",
    [StudyType.IN_VITRO_TRIAL]: "In Vitro Trial",
    [StudyType.NON_RCT_TRIAL]: "Non-RCT Experimental",
    [StudyType.LITERATURE_REVIEW]: "Literature Review",
    [StudyType.ANIMAL_STUDY]: "Animal Study",
  };
  return studyTypeLabels[type] || type;
}

function formatDomain(domain: Domain) {
  const domainLabels: Record<Domain, string> = {
    [Domain.Medicine]: "Medicine",
    [Domain.Biology]: "Biology",
    [Domain.ComputerScience]: "Computer Science",
    [Domain.Chemistry]: "Chemistry",
    [Domain.Psychology]: "Psychology",
    [Domain.Physics]: "Physics",
    [Domain.MaterialsScience]: "Materials Science",
    [Domain.Engineering]: "Engineering",
    [Domain.EnvironmentalScience]: "Environmental Science",
    [Domain.Business]: "Business",
    [Domain.Economics]: "Economics",
    [Domain.Mathematics]: "Mathematics",
    [Domain.PoliticalScience]: "Political Science",
    [Domain.AgriculturalAndFoodSciences]: "Agricultural and Food Sciences",
    [Domain.Education]: "Education",
    [Domain.Sociology]: "Sociology",
    [Domain.Geology]: "Geology",
    [Domain.Geography]: "Geography",
    [Domain.History]: "History",
    [Domain.Art]: "Art",
    [Domain.Philosophy]: "Philosophy",
    [Domain.Law]: "Law",
    [Domain.Linguistics]: "Linguistics",
  };
  return domainLabels[domain] || domain;
}
